import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { Modes } from "./types";
import { Loading } from "../shared/Loading";
import { UserDocument } from "../../models/UserDocument";
import { PDFDownloadDisclaimer } from "../../views/shared/dialog/PDFDownloadDisclaimer";
import { useAuthContext } from "AuthContextWrapper";
import { DocumentsAPI } from "apis/DocumentsAPI";
import { getDocumentContents } from "apis/getDocumentContents";

export interface Props {
  document: UserDocument;
  documentSrc: string;
  setMode: (mode: Modes) => void;
}

export const ViewUserDocument = ({
  document,
  documentSrc,
  setMode,
}: Props): JSX.Element => {
  const [bestSrc, setBestSrc] = useState<string>(documentSrc) // Start with thumbnail for images
  const [isLoadingBetterSrc, setIsLoadingBetterSrc] = useState<boolean>(false)
  const { activeEstate, user } = useAuthContext()

  useEffect(() => {
    if (!activeEstate?.id || !user?.id) {
      return
    }
  
    if (document?.version?.cloudUrl) {
      setBestSrc(document.version.cloudUrl)
      setIsLoadingBetterSrc(false)
      return
    }

    if (!document?.version?.id || !document.version?.ext || !document?.id) {
      return // don't alter image src to be full sized
    }

    // Upgrade images to the full (non-thumbnail) version
    switch (document.version.ext.toLowerCase()) {
      case "png":
      case "gif":
      case "jpg":
        setIsLoadingBetterSrc(true)
        getDocumentContents(
          activeEstate.id,
          user.id,
          document.id,
          undefined,
          undefined,
          false,
          0,
          false
        ).then(src => {
          if (src) {
            setBestSrc(src)
          }
          setIsLoadingBetterSrc(false)
        })

    }
  }, [document?.version?.id, document.version?.ext, document?.id])

  const renderUserDocument = (): JSX.Element | null | undefined => {
    if (document?.version?.cloudUrl) {
      const embed = <iframe width="100%" height="100%" scrolling="no" src={documentSrc} style={{ overflow: "auto", minHeight: "50dvh" }} />
      return embed
    }
    switch (document?.version?.ext?.toLowerCase()) {
      case "png":
      case "gif":
      case "jpg":
        return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", maxHeight: "100%", maxWidth: "100%" }}>
          <RenderImage
            document={document}
            documentSrc={bestSrc}
          />
        </div>
      case "pdf":
        return (
          <RenderPdf
            documentSrc={bestSrc}
            style={{ width: "100%", flexGrow: 1, height: "70dvh" }}
            withDisclaimer={true}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box style={{ position: "relative", width: "100%", minHeight: "300px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      {isLoadingBetterSrc && <div style={{ position: "absolute", top: 0, left: 100 }}>
        <Loading />
      </div>}
      <div style={{ flexGrow: 1, display: "flex", alignItems: "stretch", justifyContent: "stretch", width: "100%" }} >
        {renderUserDocument()}
      </div>
      <div style={{ paddingTop: "0.5rem" }}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setMode(Modes.edit)}
        >
          Back to Edit
        </Button>
      </div>
    </Box>
  );
};

const RenderImage = ({
  document,
  documentSrc,
}: {
  document: UserDocument;
  documentSrc: string;
}) => {
  return (
    <img
      src={documentSrc}
      alt={document?.version?.title}
      style={{
        maxWidth: "100%",
      }}
    />
  );
};

export const RenderPdf = ({
  documentSrc,
  style,
  withDisclaimer,
}: {
  documentSrc: string;
  style?: any;
  withDisclaimer?: boolean;
}): JSX.Element => {
  return (
    <div style={{ position: "relative", ...style, }}>
      <object
        style={{
          border: "0",
          minWidth: "300px",
          width: "100%",
          ...style,
        }}
        data={documentSrc}
        type="application/pdf"
      >
        <iframe
          src={documentSrc}
          style={{
            border: "0",
            minWidth: "300px",
            width: "100%",
            ...style,
          }}
        >
          This browser does not support PDFs. Please download the PDF to view it
        </iframe>
      </object>
      {withDisclaimer && <PDFDownloadDisclaimer />}
    </div>
  );
};
